"use client";

import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, Check } from "lucide-react";

interface AdminText {
    key: string;
    content: string;
    enabled?: boolean;
}

const AdminTextsPanel: React.FC = () => {
    const [texts, setTexts] = useState<AdminText[]>([]);
    const [selectedKey, setSelectedKey] = useState<string>("");
    const [content, setContent] = useState<string>("");
    const [enabled, setEnabled] = useState<boolean>(false);
    const [newKey, setNewKey] = useState<string>("");
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

    // Szövegek betöltése
    useEffect(() => {
        loadTexts();
    }, []);

    // Kiválasztott szöveg betöltése
    useEffect(() => {
        if (selectedKey) {
            loadText(selectedKey);
        }
    }, [selectedKey]);

    const loadTexts = async () => {
        setLoading(true);
        try {
            const response = await fetch("/api/admin/admin_texts");
            if (!response.ok) throw new Error("Hiba a szövegek betöltésénél");
            const data = await response.json();
            setTexts(data);
            if (data.length > 0 && !selectedKey) {
                setSelectedKey(data[0].key);
            }
        } catch (error) {
            console.error("Hiba:", error);
            setMessage({ type: "error", text: "Hiba a szövegek betöltésénél" });
        } finally {
            setLoading(false);
        }
    };

    const loadText = async (key: string) => {
        setLoading(true);
        try {
            const response = await fetch(`/api/admin/admin_texts/${key}`);
            if (!response.ok) throw new Error("Szöveg betöltési hiba");
            const data = await response.json();
            setContent(data.content || "");
            setEnabled(data.enabled || false);
        } catch (error) {
            console.error("Hiba:", error);
            setMessage({ type: "error", text: "Hiba a szöveg betöltésénél" });
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!selectedKey || selectedKey.trim() === "") {
            setMessage({ type: "error", text: "Válassz egy szöveget!" });
            return;
        }

        setSaving(true);
        try {
            const response = await fetch(`/api/admin/admin_texts/${selectedKey}`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "",
                },
                body: JSON.stringify({ content, enabled }),
                credentials: "include",
            });

            if (!response.ok) throw new Error("Mentési hiba");
            setMessage({ type: "success", text: "Szöveg sikeresen mentve!" });

            // Frissítjük a listát
            await loadTexts();

            // Üzenet törlése 3 másodperc után
            setTimeout(() => setMessage(null), 3000);
        } catch (error) {
            console.error("Hiba:", error);
            setMessage({ type: "error", text: "Hiba a mentés során" });
        } finally {
            setSaving(false);
        }
    };

    const handleCreateNew = async () => {
        if (!newKey.trim()) {
            setMessage({ type: "error", text: "Add meg az új szöveg kulcsát!" });
            return;
        }

        setSaving(true);
        try {
            const response = await fetch(`/api/admin/admin_texts/${newKey}`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "",
                },
                body: JSON.stringify({ content: "", enabled: false }),
                credentials: "include",
            });

            if (!response.ok) throw new Error("Létrehozási hiba");

            setNewKey("");
            setMessage({ type: "success", text: "Új szöveg létrehozva!" });
            await loadTexts();
            setTimeout(() => setMessage(null), 3000);
        } catch (error) {
            console.error("Hiba:", error);
            setMessage({ type: "error", text: "Hiba az új szöveg létrehozásakor" });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-6">
            <Card>
                <CardHeader>
                    <CardTitle>Admin szövegek kezelése</CardTitle>
                    <CardDescription>Globális szövegek szerkesztése és tárolása</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                    {/* Üzenet megjelenítés */}
                    {message && (
                        <div
                            className={`p-4 rounded-md flex items-center gap-2 ${message.type === "success"
                                ? "bg-green-500/20 text-green-400"
                                : "bg-red-500/20 text-red-400"
                                }`}
                        >
                            {message.type === "success" && <Check size={20} />}
                            {message.text}
                        </div>
                    )}

                    {/* Új szöveg létrehozása */}
                    <div className="space-y-2">
                        <h3 className="text-sm font-semibold">Új szöveg létrehozása</h3>
                        <div className="flex gap-2">
                            <Input
                                type="text"
                                placeholder="Szöveg kulcsa (pl. home_banner)"
                                value={newKey}
                                onChange={(e) => setNewKey(e.target.value)}
                                className="flex-1"
                            />
                            <Button
                                onClick={handleCreateNew}
                                disabled={saving || !newKey.trim()}
                                className="bg-primary"
                            >
                                {saving ? <Loader2 className="animate-spin" /> : "Létrehozás"}
                            </Button>
                        </div>
                    </div>

                    {/* Szövegek listája */}
                    {loading ? (
                        <div className="flex justify-center py-8">
                            <Loader2 className="animate-spin" />
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {/* Szövegek választása */}
                            <div>
                                <h3 className="text-sm font-semibold mb-2">Elérhető szövegek</h3>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                    {texts.length > 0 ? (
                                        texts.map((text) => (
                                            <button
                                                key={text.key}
                                                onClick={() => setSelectedKey(text.key)}
                                                className={`p-3 rounded-md border-2 transition-colors text-left ${selectedKey === text.key
                                                    ? "bg-primary text-primary-foreground border-primary"
                                                    : "bg-secondary hover:bg-secondary/80 border-border"
                                                    }`}
                                            >
                                                <div className="font-semibold text-sm">{text.key}</div>
                                                <div className="text-xs opacity-75 truncate">
                                                    {text.content?.substring(0, 50) || "Üres"}...
                                                </div>
                                            </button>
                                        ))
                                    ) : (
                                        <p className="text-muted-foreground">Még nincs szöveg létrehozva</p>
                                    )}
                                </div>
                            </div>

                            {/* Szöveg szerkesztés */}
                            {selectedKey && (
                                <div className="space-y-4">
                                    <div>
                                        <h3 className="text-sm font-semibold mb-2">Szöveg: {selectedKey}</h3>
                                        <Textarea
                                            value={content}
                                            onChange={(e) => setContent(e.target.value)}
                                            placeholder="Írd ide a szöveget..."
                                            rows={15}
                                            className="font-mono text-sm"
                                        />
                                    </div>

                                    <div className="flex items-center gap-4">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={enabled}
                                                onChange={(e) => setEnabled(e.target.checked)}
                                                className="w-4 h-4 rounded"
                                            />
                                            <span className="text-sm font-medium">Engedélyezve</span>
                                        </label>
                                    </div>

                                    <div className="flex gap-2 justify-end">
                                        <Button
                                            onClick={handleSave}
                                            disabled={saving}
                                            className="bg-primary"
                                        >
                                            {saving ? (
                                                <>
                                                    <Loader2 className="animate-spin mr-2" />
                                                    Mentés...
                                                </>
                                            ) : (
                                                <>
                                                    <Check className="mr-2" size={16} />
                                                    Mentés
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default AdminTextsPanel;
